import { createContext, useContext, useState, useEffect } from "react";
import api from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /auth/me check.
    // AuthCallback exchanges the session_id and hard-navigates to /dashboard which
    // re-mounts the app cleanly.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (token) {
      api.get("/auth/me")
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem("token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.access_token);
    setUser(res.data.user);
    
    // Check if password change is required
    if (res.data.must_change_password) {
      setMustChangePassword(true);
    } else if (!res.data.has_seen_tutorial) {
      // Show tutorial only if password doesn't need to be changed
      setShowTutorial(true);
    }
    
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.post("/auth/register", data);
    localStorage.setItem("token", res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setMustChangePassword(false);
    setShowTutorial(false);
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
  };

  const onPasswordChanged = () => {
    setMustChangePassword(false);
    // After password change, check if tutorial should be shown
    setShowTutorial(true);
  };

  const onTutorialComplete = () => {
    setShowTutorial(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      loading, 
      login, 
      register, 
      logout, 
      refreshUser,
      mustChangePassword,
      showTutorial,
      onPasswordChanged,
      onTutorialComplete
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
